import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMerchants } from "features/merchants/useMerchants";
import { useMerchantBalances } from "features/merchants/useMerchantBalances";
import type { MerchantRecord } from "components/merchant-row/MerchantRow";
import type { MerchantApiRecord } from "utils/AxiosUtil";
import { StatusVariant } from "components/status-badge/StatusBadge";

// MerchantStatusCases in PaymentsVIPv2.sol: UNASSIGNED, ACTIVE, REVOKED, SUSPENDED, BLOCKED.
// 0 is deliberately absent — an unassigned id is not a merchant, and lookupMerchant reports it
// as a miss rather than a row.
const merchantStatusMap: Record<number, StatusVariant> = {
  1: "Active",
  2: "Revoked",
  3: "Suspended",
  4: "Blocked",
};

export function getMerchantStatus(status: number): StatusVariant {
  return merchantStatusMap[status] ?? "Unknown";
}

export interface UseIssuerMerchantsReturn {
  merchants: MerchantRecord[];
  merchantsMap: Record<string, string>;
  activeMerchants: number;
  loadingMerchants: boolean;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useIssuerMerchants(): UseIssuerMerchantsReturn {
  const queryClient = useQueryClient();

  const {
    data: rawMerchants,
    isLoading: merchantsLoading,
    error: merchantsError,
  } = useMerchants();

  const addresses = useMemo(
    () => rawMerchants?.map((m: MerchantApiRecord) => m.merchantAddress) ?? [],
    [rawMerchants],
  );

  const { data: balances, isLoading: balancesLoading } =
    useMerchantBalances(addresses);

  const merchants: MerchantRecord[] = useMemo(
    () =>
      rawMerchants?.map((m: MerchantApiRecord) => ({
        ...m,
        logoLetter: m.merchantName[0],
        balance: balances?.[m.merchantAddress],
        status: getMerchantStatus(m.merchantStatus),
      })) ?? [],
    [rawMerchants, balances],
  );
  const merchantsMap = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        (rawMerchants ?? []).map((merchant: MerchantApiRecord) => [
          merchant.merchantId,
          merchant.merchantName,
        ]),
      ),
    [rawMerchants],
  );

  const activeMerchants = useMemo(
    () => merchants.filter((m) => m.merchantStatus === 1).length,
    [merchants],
  );

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["merchants"] });
    queryClient.invalidateQueries({ queryKey: ["balances", "merchants"] });
    // A merchant found by the on-chain fallback isn't in the recent list, so refetching that
    // list can't refresh it — without this its row keeps offering "Sign up" after signup.
    queryClient.invalidateQueries({ queryKey: ["merchant-lookup"] });
    // The KPI tile is a separate query from the list, and the list is capped at the recent 10
    // while the tile counts every merchant — so refetching the list moves one and not the other.
    queryClient.invalidateQueries({ queryKey: ["merchantsCount"] });
    // Platform + live/sandbox mode is fetched per row and cached per merchantId. Signup is what
    // creates it, so without this a just-signed-up row still reads "Not set up" when reopened.
    queryClient.invalidateQueries({ queryKey: ["merchantSettlement"] });
  };

  return {
    merchants,
    merchantsMap,
    activeMerchants,
    loadingMerchants: merchantsLoading,
    isLoading: merchantsLoading || balancesLoading,
    error: merchantsError ? String(merchantsError) : null,
    refresh,
  };
}
